import { browser } from "$app/environment";
import { goto } from "$app/navigation";
import { page } from "$app/stores";

import { derived, get } from "svelte/store";

export interface AppDashboardParams {
  start?: number;
  end?: number;
  live?: boolean;
  functionCallId?: string;
  inputId?: string;
  activeTab?: string;
  // Note: This should be the _root_ function ID (base_function_id or function_id), which different
  // for parametrized functions than the function ID.
  functionId?: string;
  containerId?: string;
  sandboxId?: string;
  functionSection?: string;
  tableFilter?: string;
  methodName?: string;
  limit?: number;
  orderBy?: string;
  direction?: string;
  source?: string;
  logId?: string;
  containerStatusFilter?: string;
  searchText?: string;
  // This will be added to the URL when the user clicks "View in context" from the log drawer
  // and will be used to fetch the log context in <LogContextWrapper>.
  includeLogContext?: boolean;
}

// Parse URL parameters into typed dashboard params
export const appDashboardParams = derived(page, ($page) => {
  if (!$page.url) return {};

  const searchParams = new URLSearchParams($page.url.searchParams);

  return {
    start: searchParams.has("start")
      ? parseFloat(searchParams.get("start")!)
      : undefined,
    end: searchParams.has("end")
      ? parseFloat(searchParams.get("end")!)
      : undefined,
    live: searchParams.has("live")
      ? searchParams.get("live") === "true"
      : false,
    functionCallId: searchParams.get("fcId") || undefined,
    inputId: searchParams.get("inputId") || undefined,
    activeTab: searchParams.get("activeTab") || undefined,
    functionId: searchParams.get("functionId") || undefined,
    containerId: searchParams.get("containerId") || undefined,
    sandboxId: searchParams.get("sandboxId") || undefined,
    functionSection: searchParams.get("functionSection") || undefined,
    tableFilter: searchParams.get("tableFilter") || undefined,
    methodName: searchParams.get("methodName") || undefined,
    limit: searchParams.has("limit")
      ? parseInt(searchParams.get("limit")!)
      : undefined,
    orderBy: searchParams.get("order") || undefined,
    direction: searchParams.get("d") || undefined,
    containerStatusFilter: searchParams.get("cFilter") || undefined,
    source: searchParams.get("source") || undefined,
    logId: searchParams.get("logId") || undefined,
    includeLogContext: searchParams.has("includeLogContext")
      ? searchParams.get("includeLogContext") === "true"
      : false,
    searchText: searchParams.get("searchText")
      ? decodeURIComponent(searchParams.get("searchText")!)
      : "",
  } as AppDashboardParams;
});

/** Updater for parameters on the dashboard. */
export type ParamsUpdate = (
  current: AppDashboardParams,
) => Partial<AppDashboardParams>;

/** Get a URL suitable for linking to a page with updated parameters. */
export function getAppDashboardUrl(...updates: ParamsUpdate[]): string {
  let params = get(appDashboardParams);
  for (const updater of updates) {
    params = updater(params);
  }

  const currentParams = new URLSearchParams();
  const baseUrl = window.location.pathname;

  const paramsMap = [
    { key: "start", value: params.start?.toString() },
    { key: "end", value: params.end?.toString() },
    { key: "live", value: params.live?.toString() },
    { key: "fcId", value: params.functionCallId },
    { key: "inputId", value: params.inputId },
    { key: "activeTab", value: params.activeTab },
    { key: "functionId", value: params.functionId },
    { key: "containerId", value: params.containerId },
    { key: "sandboxId", value: params.sandboxId },
    { key: "functionSection", value: params.functionSection },
    { key: "tableFilter", value: params.tableFilter },
    { key: "methodName", value: params.methodName },
    { key: "limit", value: params.limit?.toString() },
    { key: "order", value: params.orderBy },
    { key: "d", value: params.direction },
    { key: "cFilter", value: params.containerStatusFilter },
    { key: "source", value: params.source },
    { key: "logId", value: params.logId },
    { key: "includeLogContext", value: params.includeLogContext?.toString() },
    {
      key: "searchText",
      value: params.searchText
        ? encodeURIComponent(params.searchText)
        : undefined,
    },
  ];

  paramsMap.forEach(({ key, value }) => {
    if (value !== undefined) {
      currentParams.set(key, value);
    }
  });

  const queryString = currentParams.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}

// Private state for batching updates to dashboard params.
//
// Do not export this variable. This is used by `updateAppDashboardParams()`
// since multiple event handlers might run at the same time, and we want to
// batch together their redirects into a single `goto()` call. Otherwise, two
// redirects may happen at the same time, and the earlier one will be overriden
// by the later one.
//
// This is used by the function call drawer on the app dashboard. If the drawer
// is closed by clicking outside it on a `Contianers` section navigation, that
// triggers two updates to the URL. We want both to go through.
const queuedUpdates: ParamsUpdate[] = [];

/**
 * Update the app dashboard params.
 *
 * If there are multiple updates in a single event loop tick, they will be
 * batched together. This is done with `setTimeout(..., 0)`.
 */
export function updateAppDashboardParams(
  updater: (current: AppDashboardParams) => Partial<AppDashboardParams>,
  options: { replaceState?: boolean; keepFocus?: boolean } = {},
) {
  if (!browser) return;
  queuedUpdates.push(updater);

  setTimeout(() => {
    // On the next event loop iteration, flush all of the queued updates.
    if (queuedUpdates.length === 0) return;
    const updates = queuedUpdates.splice(0, queuedUpdates.length); // Clear out the updates.
    const url = getAppDashboardUrl(...updates);
    let replaceState = options.replaceState ?? false;

    // going to the same URL shouldn't add another history entry, otherwise clicking on the same tab
    // will cause a problem where user will have to click back multiple times to change the view.
    if (url === window.location.pathname + window.location.search) {
      replaceState = true;
    }

    goto(url, {
      replaceState,
      keepFocus: options.keepFocus,
      noScroll: true,
    });
  }, 0);
}

export const duration = derived(appDashboardParams, ($appDashboardParams) => {
  if (
    $appDashboardParams.start !== undefined &&
    $appDashboardParams.end !== undefined
  ) {
    return $appDashboardParams.end - $appDashboardParams.start;
  }
  return undefined;
});

export const calculateBucketSize = (
  duration: number,
  numBuckets: number = 100,
) => {
  // Note: Bucket sizes must be even numbers of seconds, and they also must
  // divide evenly into the nearest time unit. For example, 20 or 30 seconds is
  // okay (divides into 60), but 40 is not. Similarly 40 minutes is not okay.
  const bucketSizes = [
    2, 4, 6, 12, 20, 30, 60, 120, 180, 240, 300, 360, 600, 720, 900, 1200, 1800,
  ];
  bucketSizes.push(...[1, 2, 3, 4, 8, 12, 24].map((h) => h * 3600));

  // Return the smallest bucket size that produces at most numBuckets.
  for (const size of bucketSizes) {
    if (Math.round(duration / size) <= numBuckets) return size;
  }
  return bucketSizes[bucketSizes.length - 1];
};

export const bucketSize = derived(duration, ($duration) => {
  if (!$duration) {
    return undefined;
  }
  return calculateBucketSize($duration);
});
